// Static, evergreen long-form content for blog posts.
// Each entry is keyed by post slug; the renderer falls back to the post excerpt
// followed by a generic article when no slug-specific content exists.

const GENERIC_ARTICLE = (excerpt) => `
${excerpt}

## Why This Matters

Building lifelong capability is not about quick wins. It is about establishing
the strength, mobility and resilience needed to remain independent, capable and
engaged with life — at every age.

## The Zeeva Approach

Our work is grounded in evidence-based exercise science, rehabilitation
research and longevity literature. Programs combine:

- Progressive strength training
- Mobility and movement quality work
- Recovery and adaptation protocols
- Nutritional foundations
- Lifestyle and behavioural support

## Practical Foundations

### 1. Establish Your Baseline
You cannot improve what you do not measure. Start with a capability assessment.

### 2. Train Progressively
Progressive overload — gradually increasing demand — is the single most
important principle in physical adaptation.

### 3. Prioritise Recovery
Adaptation happens during recovery. Sleep, stress management and nutrition are
non-negotiable.

### 4. Build Sustainably
Consistency over intensity. The best program is the one you can repeat for
years.

## Getting Started

Take the Zeeva Capability Assessment to understand your baseline. Speak with a
specialist about the program that matches your goals and current capability.
`;

const CONTENT = {
  'strength-training-longevity': `
Strength training is the single most evidence-backed intervention for healthspan,
mobility and independence as you age.

## Why Strength Wins

After age 30, untrained adults lose 3–8% of muscle mass per decade. After 60 the
decline accelerates. Sarcopenia — age-related muscle loss — is one of the
strongest predictors of frailty, falls and loss of independence.

The fix is straightforward: progressive resistance training, performed
consistently, two to four times per week.

## What the Evidence Shows

- Reduces all-cause mortality risk
- Improves bone density and reduces fracture risk
- Supports metabolic health and insulin sensitivity
- Maintains cognitive function and mood
- Preserves the capacity to do everyday things — climb stairs, carry groceries,
  rise from a chair

## Practical Starting Points

### Begin with the Big Patterns
- Squat and hinge
- Push and pull
- Carry and brace

### Progress Slowly
Start lighter than you think. Add load weekly, never sacrificing form.

### Build the Habit First
Two well-executed sessions per week, repeated for years, beat any 12-week
intensive that you cannot sustain.

## The Bottom Line

Strength is the master quality. It underpins mobility, balance, metabolic health
and confidence. Build it now and protect it for life.
`,
  'mobility-versus-flexibility': `
Mobility and flexibility are often used interchangeably — but they are not the
same thing, and training them differently transforms how you move.

## The Difference

- **Flexibility** is the passive range available at a joint.
- **Mobility** is the active, controlled range you can use, under load, in
  real movement.

You can be flexible without being mobile. Mobility is the more useful quality.

## Why Mobility Matters

Mobility determines how you move every day:
- How efficiently you walk, sit, squat and reach
- How resilient your joints are to load and stress
- How quickly you recover from awkward positions
- How well you express strength

## Training Mobility Effectively

### 1. Combine Range with Strength
Loaded mobility work — controlled articular rotations, end-range strength work
— produces lasting change.

### 2. Train Joint by Joint
Address the hips, thoracic spine, shoulders and ankles as discrete priorities.

### 3. Be Consistent
A few minutes daily beats long sessions weekly.

## What to Expect

Done well, mobility work pays back in:
- Less stiffness and pain
- Easier daily movement
- Better performance in strength training
- Reduced injury risk

## Closing Thought

Stretching alone rarely produces durable change. Active, loaded mobility work
does. Make it part of your weekly practice for life.
`,
};

export const getBlogContent = (post) =>
  CONTENT[post.slug] || GENERIC_ARTICLE(post.excerpt);
